import * as React from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import {
  CheckCircle2,
  Clock3,
  Download,
  Expand,
  FileCheck,
  FileX,
  RotateCcw,
  Trash,
  Upload,
} from 'lucide-react';
import type {
  DeliveryVoucher,
  DeliveryVoucherIndicatorType,
  InvoiceDeliveryVoucher,
} from 'contracts/DeliveryVouchers';
import { Badge } from 'components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'components/ui/tooltip';
import { cn } from 'lib/utils';
import { DataTableHelper, Formatter } from 'utils';

import {
  getDeliveryVoucherInvoiceDestination,
  getDeliveryVoucherInvoiceOrigin,
  getLatestDeliveryVoucher,
  getDeliveryVoucherStatus,
} from '../utils';

/*
Fluxo das colunas:
1. As primeiras colunas exibem os dados principais da nota entregue.
2. A coluna Status usa o último comprovante ativo para definir o indicador visual.
3. A coluna Ações monta os ícones disponíveis de acordo com o status do comprovante.
*/

const dt = new DataTableHelper<InvoiceDeliveryVoucher>();

const STATUS_PROPERTIES: Record<
  DeliveryVoucherIndicatorType,
  { label: string; className: string; icon: React.ReactNode }
> = {
  approvedManual: {
    label: 'Aprovado manualmente',
    className: 'bg-emerald-500 text-white',
    icon: <CheckCircle2 className="h-3.5 w-3.5" />,
  },
  approvedAutomatic: {
    label: 'Aprovado automaticamente',
    className: 'bg-sky-500 text-white',
    icon: <CheckCircle2 className="h-3.5 w-3.5" />,
  },
  waitingApproval: {
    label: 'Aguardando aprovação',
    className: 'bg-blue-500 text-white',
    icon: <FileCheck className="h-3.5 w-3.5" />,
  },
  waitingVoucher: {
    label: 'Aguardando comprovante',
    className: 'bg-orange-500 text-white',
    icon: <Clock3 className="h-3.5 w-3.5" />,
  },
  voucherRejected: {
    label: 'Reprovado',
    className: 'bg-red-500 text-white',
    icon: <FileX className="h-3.5 w-3.5" />,
  },
};

type VoucherAction = {
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  className?: string;
};

const ActionIconButton = ({
  action,
}: {
  action: VoucherAction;
}) => {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label={action.label}
            disabled={action.disabled}
            onClick={event => {
              event.stopPropagation();
              if (!action.disabled) action.onClick();
            }}
            className={cn(
              'flex h-7 w-7 items-center justify-center rounded-md text-slate-600 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-40',
              action.className
            )}
          >
            {action.icon}
          </button>
        </TooltipTrigger>
        <TooltipContent side="top">{action.label}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

type Props = {
  onUpload: (invoice: InvoiceDeliveryVoucher) => void;
  onView: (voucher: DeliveryVoucher) => void;
  onDownload: (voucher: DeliveryVoucher) => void;
  onApprove: (voucher: DeliveryVoucher) => void;
  onReject: (voucher: DeliveryVoucher) => void;
  onCancelApproval: (voucher: DeliveryVoucher) => void;
  onDelete: (voucher: DeliveryVoucher) => void;
  actionsDisabled?: boolean;
  canDownload: boolean;
  canUpdate: boolean;
  canDelete: boolean;
};

type VoucherActionsParams = Props & {
  invoice: InvoiceDeliveryVoucher;
  status: DeliveryVoucherIndicatorType;
  voucher?: DeliveryVoucher;
};

// Cria a ação de upload usada por notas sem comprovante e comprovantes reprovados.
const createUploadAction = ({
  invoice,
  onUpload,
  actionsDisabled,
  label,
}: Pick<VoucherActionsParams, 'invoice' | 'onUpload' | 'actionsDisabled'> & {
  label: string;
}): VoucherAction => ({
  label,
  disabled: actionsDisabled,
  onClick: () => onUpload(invoice),
  icon: <Upload className="h-5 w-5" />,
  className: 'text-slate-900 hover:text-primary-500',
});

// Centraliza quais ações aparecem em cada status do último comprovante ativo.
const getVoucherActions = ({
  invoice,
  status,
  voucher,
  actionsDisabled,
  onUpload,
  onView,
  onDownload,
  onApprove,
  onReject,
  onCancelApproval,
  onDelete,
  canDownload,
  canUpdate,
  canDelete,
}: VoucherActionsParams): VoucherAction[] => {
  if (!voucher || status === 'waitingVoucher') {
    return [
      createUploadAction({
        invoice,
        onUpload,
        actionsDisabled,
        label: 'Inserir comprovante',
      }),
    ];
  }

  const commonActions: VoucherAction[] = canDownload ? [
    {
      label: 'Visualizar comprovante',
      disabled: actionsDisabled,
      onClick: () => onView(voucher),
      icon: <Expand className="h-5 w-5" />,
      className: 'text-slate-900 hover:text-primary-500',
    },
    {
      label: 'Download',
      disabled: actionsDisabled,
      onClick: () => onDownload(voucher),
      icon: <Download className="h-5 w-5" />,
      className: 'text-slate-900 hover:text-primary-500',
    },
  ] : [];

  const deleteActions: VoucherAction[] = canDelete ? [{
    label: 'Excluir comprovante',
    disabled: actionsDisabled,
    onClick: () => onDelete(voucher),
    icon: <Trash className="h-5 w-5" />,
    className: 'text-red-600 hover:text-red-700',
  }] : [];

  if (status === 'waitingApproval') {
    return [
      ...commonActions,
      ...(canUpdate ? [{
        label: 'Aprovar comprovante',
        disabled: actionsDisabled,
        onClick: () => onApprove(voucher),
        icon: <FileCheck className="h-5 w-5" />,
        className: 'text-emerald-600 hover:text-emerald-700',
      },
      {
        label: 'Reprovar comprovante',
        disabled: actionsDisabled,
        onClick: () => onReject(voucher),
        icon: <FileX className="h-5 w-5" />,
        className: 'text-red-600 hover:text-red-700',
      }] : []),
      ...deleteActions,
    ];
  }

  if (status === 'voucherRejected') {
    return [
      createUploadAction({
        invoice,
        onUpload,
        actionsDisabled,
        label: 'Inserir novo comprovante',
      }),
      ...commonActions,
      ...deleteActions,
    ];
  }

  return [
    ...commonActions,
    ...(canUpdate ? [{
      label: 'Cancelar aprovação',
      disabled: actionsDisabled,
      onClick: () => onCancelApproval(voucher),
      icon: <RotateCcw className="h-5 w-5" />,
      className: 'text-amber-600 hover:text-amber-700',
    }] : []),
    ...deleteActions,
  ];
};

// Renderiza os ícones com tooltip mantendo a célula da tabela mais simples.
const ActionIconList = ({ actions }: { actions: VoucherAction[] }) => (
  <div className="flex items-center gap-1">
    {actions.map(action => (
      <ActionIconButton key={action.label} action={action} />
    ))}
  </div>
);

export const useDeliveryVoucherColumns = ({
  onUpload,
  onView,
  onDownload,
  onApprove,
  onReject,
  onCancelApproval,
  onDelete,
  actionsDisabled,
  canDownload,
  canUpdate,
  canDelete,
}: Props): ColumnDef<InvoiceDeliveryVoucher>[] => {
  // As colunas usam helpers externos para manter a célula de status e ações mais legível.
  return React.useMemo(
    () => [
      dt.buildColumn({
        id: 'NF',
        accessorKey: 'infnfeIdeNnf',
        size: 80,
        content: row => [row.infnfeIdeNnf, row.infnfeIdeSerie].join('-'),
      }),
      dt.buildColumn({
        id: 'Emissão',
        accessorKey: 'infnfeIdeDhemi',
        size: 92,
        content: row => Formatter.date(row.infnfeIdeDhemi),
      }),
      dt.buildColumn({
        id: 'Prazo',
        accessorKey: 'deadlineDate',
        size: 92,
        content: row => Formatter.date(row.deadlineDate),
      }),
      dt.buildColumn({
        id: 'Entrega',
        accessorKey: 'deliveryDate',
        size: 92,
        content: row => Formatter.date(row.deliveryDate),
      }),
      dt.buildColumn({
        id: 'Fornecedor',
        accessorFn: row => row.supplier?.id,
        content: row => row.supplier?.tradeName || '---',
      }),
      dt.buildColumn({
        id: 'Centro',
        accessorFn: row => row.company?.id,
        content: row => row.company?.tradeName || '---',
      }),
      dt.buildColumn({
        id: 'Origem',
        accessorFn: row => row.originCity?.id,
        content: row => getDeliveryVoucherInvoiceOrigin(row) || '---',
      }),
      dt.buildColumn({
        id: 'Destino',
        accessorFn: row => row.destinationCity?.id,
        content: row => getDeliveryVoucherInvoiceDestination(row) || '---',
      }),
      dt.buildColumn({
        id: 'Transportadora',
        accessorFn: row => row.carrier?.id,
        content: row => row.carrier?.tradeName || '---',
      }),
      dt.buildColumn({
        id: 'Status',
        enableSorting: false,
        enableHiding: false,
        accessorFn: row => getDeliveryVoucherStatus(row),
        content: row => {
          const status = getDeliveryVoucherStatus(row);
          const properties = STATUS_PROPERTIES[status];

          return (
            <Badge
              aria-label={properties.label}
              className={cn(
                'group relative inline-flex h-7 w-7 items-center justify-center rounded-full border-0 p-0 shadow-none',
                properties.className
              )}
            >
              <span className="absolute -top-8 left-1/2 z-20 hidden w-max -translate-x-1/2 rounded bg-slate-700 px-2 py-1 font-GilroySemibold text-[11px] leading-none text-white shadow-sm group-hover:flex">
                {properties.label}
              </span>
              {properties.icon}
            </Badge>
          );
        },
      }),
      dt.buildColumn({
        id: 'Ações',
        size: 170,
        enableSorting: false,
        enableHiding: false,
        content: row => {
          const status = getDeliveryVoucherStatus(row);
          const voucher = getLatestDeliveryVoucher(row);

          return (
            <ActionIconList
              actions={getVoucherActions({
                invoice: row,
                status,
                voucher,
                actionsDisabled,
                canDownload,
                canUpdate,
                canDelete,
                onUpload,
                onView,
                onDownload,
                onApprove,
                onReject,
                onCancelApproval,
                onDelete,
              })}
            />
          );
        },
      }),
    ],
    [
      actionsDisabled,
      canDownload,
      canUpdate,
      canDelete,
      onApprove,
      onCancelApproval,
      onDelete,
      onDownload,
      onReject,
      onUpload,
      onView,
    ]
  );
};
